import type { PropsWithChildren } from 'react';

import { Column } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface SortableHeaderBaseProps<TData> {
  column: Column<TData, unknown>;
  className?: string;
  ariaLabel?: string;
}

type SortableHeaderProps<TData> = PropsWithChildren<SortableHeaderBaseProps<TData>>;

/**
 * Sortable table header component for TanStack Table columns.
 *
 * Provides an accessible button for column sorting with visual feedback
 * for three sort states: ascending, descending, and unsorted.
 */
export function SortableHeader<TData>({
  column,
  children,
  className,
  ariaLabel,
}: SortableHeaderProps<TData>) {
  const { t } = useTranslation();
  const sortDirection = column.getIsSorted();

  const metaLabel = (column.columnDef.meta as { label?: string } | undefined)?.label;
  const columnLabel =
    ariaLabel ?? (typeof children === 'string' ? children : (metaLabel ?? column.id));

  return (
    <button
      type="button"
      onClick={() => column.toggleSorting()}
      className={cn(
        'inline-flex items-center gap-2',
        'text-sm font-medium whitespace-nowrap text-muted-foreground',
        'transition-colors hover:text-foreground',
        'rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none',
        className
      )}
      aria-label={t('table.sort_by', { 0: columnLabel })}
    >
      {children}
      {sortDirection === 'desc' ? (
        <ArrowDownIcon size={16} aria-hidden="true" />
      ) : sortDirection === 'asc' ? (
        <ArrowUpIcon size={16} aria-hidden="true" />
      ) : (
        <ChevronsUpDownIcon size={16} aria-hidden="true" />
      )}
    </button>
  );
}
